import type { AdminSpendPoint } from '@steado/admin-contract/overview';
import { memo } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import {
  ChartContainer,
  ChartDataTable,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { formatUsd } from '@/lib/format-spend';
import { formatDate } from '@/lib/format';

interface CostChartProps {
  readonly series: readonly AdminSpendPoint[];
  readonly windowDays: number;
}

const CONFIG: ChartConfig = {
  costUsd: { label: 'Cost', color: 'var(--color-chart-1)' },
};

/**
 * What the models actually cost, per day.
 *
 * The series already carried `costUsd` and nothing drew it, so the console
 * could show a month of token volume without ever showing the bill. Tokens and
 * dollars do not move together — a routing change to a pricier model moves the
 * second line and not the first — which is the whole reason this is its own
 * chart rather than a third axis on the token one.
 *
 * A day OpenRouter priced nothing is `null`, and it stays a gap. Recharts skips
 * a null rather than drawing it at the baseline, which is the behaviour wanted
 * here: a bar of height zero would claim the day was free, and the truth is
 * that nothing reported a price.
 */
export const CostChart = memo(function CostChart({ series, windowDays }: CostChartProps) {
  const caption = `Daily model cost over ${String(windowDays)} days`;
  const anyPriced = series.some((point) => point.costUsd !== null);

  if (!anyPriced) {
    return (
      <p className="py-8 text-center text-[13px] text-muted-foreground">
        No day in this window reported a cost, so there is nothing to plot. That is not the same as the calls
        having been free.
      </p>
    );
  }

  return (
    <figure aria-label={caption} className="m-0 flex flex-col gap-3">
      <ChartContainer config={CONFIG} aria-hidden="true" className="h-[180px] w-full">
        <BarChart data={[...series]} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="var(--color-border)" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(value: string) => formatDate(value)}
            tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
            tickLine={false}
            axisLine={{ stroke: 'var(--color-border)' }}
            minTickGap={24}
          />
          <YAxis
            tickFormatter={(value: number) => formatUsd(value)}
            tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
            tickLine={false}
            axisLine={false}
            width={60}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                indicator="dot"
                labelFormatter={(label) => (typeof label === 'string' ? formatDate(label) : '')}
                formatter={(value) => (
                  <span className="flex flex-1 justify-between gap-3">
                    <span className="text-muted-foreground">Cost</span>
                    <span className="font-mono font-medium text-foreground tabular-nums">
                      {typeof value === 'number' ? formatUsd(value) : String(value)}
                    </span>
                  </span>
                )}
              />
            }
          />
          <Bar dataKey="costUsd" name="costUsd" fill="var(--color-chart-1)" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ChartContainer>

      <figcaption className="sr-only">{caption}</figcaption>

      <ChartDataTable
        caption={caption}
        columns={['Date', 'Cost']}
        rows={series.map((point) => ({
          key: point.date,
          cells: [point.date, formatUsd(point.costUsd)],
        }))}
      />
    </figure>
  );
});
